import { useCallback, useEffect, useRef, useState } from "react"
import {
  captureCbeBirrPlusToken,
  isCbeBirrPlusAvailable,
  isCbeBirrPlusPaymentSuccess,
  payWithCbeBirrPlus,
} from "@/lib/cbe-birr-plus"
import type { CbeBirrPlusPaymentResult } from "@/types/cbe-birr-plus"

type CbeBirrPlusStatus = "idle" | "paying" | "success" | "error"

export type PayWithCbeBirrPlusArgs = {
  transactionId: string
  amount: number
  title: string
}

/**
 * React hook for CBE Birr Plus Mini App payments.
 *
 * Flow:
 * 1. User taps Pay → backend createChechePayment (hash + CBE /auth/pay)
 * 2. window.myJsChannel.postMessage({ token }) → native CBE Birr Plus popup
 * 3. CBE callback hits backend; frontend polls until paid
 */
export function useCbeBirrPlusPayment() {
  const [available, setAvailable] = useState(false)
  const [status, setStatus] = useState<CbeBirrPlusStatus>("idle")
  const [error, setError] = useState<string | null>(null)
  const [lastResult, setLastResult] = useState<CbeBirrPlusPaymentResult | null>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    console.log("[CBE Birr Plus] hook mount")
    captureCbeBirrPlusToken()
    const availableNow = isCbeBirrPlusAvailable()
    console.log("[CBE Birr Plus] hook initial available", availableNow)
    setAvailable(availableNow)

    // Flutter may inject myJsChannel shortly after the WebView finishes loading
    const timers = [100, 500, 1000, 2000].map((ms) =>
      setTimeout(() => {
        if (!mountedRef.current) return
        console.log(`[CBE Birr Plus] hook retry ${ms}ms`)
        captureCbeBirrPlusToken()
        const later = isCbeBirrPlusAvailable()
        console.log(`[CBE Birr Plus] hook retry ${ms}ms available`, later)
        setAvailable(later)
      }, ms)
    )

    return () => {
      mountedRef.current = false
      timers.forEach(clearTimeout)
    }
  }, [])

  const refreshAvailability = useCallback(() => {
    captureCbeBirrPlusToken()
    const isAvailable = isCbeBirrPlusAvailable()
    setAvailable(isAvailable)
    return isAvailable
  }, [])

  const pay = useCallback(async (args: PayWithCbeBirrPlusArgs): Promise<CbeBirrPlusPaymentResult> => {
    console.log("[CBE Birr Plus] hook.pay called", args)
    if (!isCbeBirrPlusAvailable()) {
      const err = new Error("CBE_BIRR_PLUS_UNAVAILABLE")
      console.error("[CBE Birr Plus] hook.pay blocked — unavailable", err)
      setStatus("error")
      setError(err.message)
      throw err
    }

    setStatus("paying")
    setError(null)
    setLastResult(null)

    try {
      const result = await payWithCbeBirrPlus({
        transactionId: args.transactionId,
        amount: args.amount,
        title: args.title,
      })

      console.log("[CBE Birr Plus] hook.pay result", result)
      if (!mountedRef.current) return result
      setLastResult(result)

      if (isCbeBirrPlusPaymentSuccess(result)) {
        console.log("[CBE Birr Plus] hook.pay SUCCESS")
        setStatus("success")
      } else {
        console.error("[CBE Birr Plus] hook.pay FAILED without confirmation", result)
        setStatus("error")
        setError("CBE_BIRR_PAYMENT_FAILED")
      }

      return result
    } catch (err: any) {
      console.error("[CBE Birr Plus] hook.pay ERROR", err?.message, err)
      if (mountedRef.current) {
        setStatus("error")
        setError(err?.message || "CBE Birr Plus payment failed")
      }
      throw err
    }
  }, [])

  return {
    available,
    status,
    error,
    lastResult,
    pay,
    isCbeBirrPlusPaymentSuccess,
    refreshAvailability,
  }
}
